import {useEffect, useRef, useState} from 'react';
import ContactInformationComponent from "../FormElements/ContactInformationComponent.tsx";
import FormFieldsetComponent from "../FormElements/FormFieldsetComponent.tsx";
import {useCustomer} from "../../../../layouts/SelfcareLayout.tsx";
import {FlowData, ProductInstance, SELFCARE_REPORT_FLOW_TYPE, RegisterFunction} from "../../../../util/types.ts";
import {imageMap} from "../../../../images/imageMap.ts";
import DescriptionTextFieldComponent from '../FormElements/DescriptionTextFieldComponent.tsx';
import OwnRouterComponent from "../FormElements/OwnRouterComponent.tsx";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import AttachFilesComponent from "../FormElements/AttachFilesComponent.tsx";
import {DispatchResetErrorReportPageEvent} from "../../../../util/events.ts";
import ErrorFlowButtons from "../FormElements/ErrorFlowButtonsComponent.tsx";
import {ProductId, submitFormFunction} from "../../../../util/SelfcareErrorReportTypes.ts";
import {filterGponSerialIllegalCharcters, scrollToElement, registerComponentsWithValidation, anyComponentsWithValidationError} from "../../../../util/helpers.ts";

export default function FiberOfflineFlow({product, submitFunction}: { product: ProductInstance, submitFunction: submitFormFunction }) {
    const {customer} = useCustomer();


    const [flowData, setFlowData] = useState<FlowData>({
        flowtype: SELFCARE_REPORT_FLOW_TYPE.OFFLINE,
        technology: product?.product.att_statistics_technology,
        email: customer.customer_contact.email,
        phone: customer.customer_contact.mobile_no,
        selectedContact: {email: false, phone: false},
        editingContact: {email: false, phone: false},
        fiberBoxLights: undefined,
        gponSerial: '',
        isCableConnected: null,
        descriptionText: '',
        ownRouter: null,
        ownRouterLights: '',
        attachedFiles: [] as File[],
    });

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string>('');

    const handleSubmit = async () => {
        setSubmitError('');

        if (anyComponentsWithValidationError(flowData)) {
            return;
        }

        setSubmitting(true)
        submitFunction(customer.customer_id, product.product_instance_id, flowData)
            .then((result) => {
                setSubmitError(result)
                setSubmitting(false)
            })
    };

    return (
        <>
            <FormFieldsetComponent
                number={2}
                title={'Opsætning'}
                className={'equipment-setup'}
            >
                <ShowLanPort
                    product={product}
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />

                {product.parent_product_id === ProductId.FbsaProductId && (
                    <EnterFiberGponNumber
                        flowData={flowData}
                        setFlowData={setFlowData}
                    />
                )}

                <FiberboxLightsTextField
                    flowData={flowData}
                    setFlowData={setFlowData}
                    product={product}
                    register={registerComponentsWithValidation}
                />

                <OwnRouterComponent
                    product={product}
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={3}
                title={'Uddyb'}
                className={'explain'}
            >
                <DescriptionTextFieldComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    placeholder={'Eks. "Ingen af mine enheder har kunnet tilgå internettet siden i går"'}
                    register={registerComponentsWithValidation}
                />
                <AttachFilesComponent
                    setFlowData={setFlowData}
                    product={product}
                    flow={SELFCARE_REPORT_FLOW_TYPE.OFFLINE}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={4}
                title={'Kontakt'}
                className={'contact'}
            >
                <ContactInformationComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
                {submitError.length > 0 &&
                    <div className={`input-container error`}>
                        <p className={'error-message'}>{submitError}</p>
                    </div>
                }
                <ErrorFlowButtons
                    onBack={() => DispatchResetErrorReportPageEvent()}
                    onSubmit={handleSubmit}
                    loading={submitting}
                />
            </FormFieldsetComponent>
        </>
    );
}

function ShowLanPort({
                         product,
                         flowData,
                         setFlowData,
                         register,
                     }: {
    product: ProductInstance;
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}) {
    const lanPortTopCmponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [cableError, setCableError] = useState<string>('');

    useEffect((): void => {
        register("ShowLanPort",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (!flowData.isCableConnected) {
                        if (flowData.isCableConnected === null) {
                            setCableError("Angiv om dine kabler sidder i de korrekte porte.");
                        } else {
                            setCableError("Sæt kablet i den korrekte port for at fortsætte.");
                        }
                        return true;
                    }
                    return false;
                },
                scrollToError: (): void => {
                    scrollToElement(lanPortTopCmponentTop.current)
                },
            },
        )
    }, [register]);

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'yes';
        setFlowData((prev) => ({ ...prev, isCableConnected: value }));
        if (value) {
            setCableError('');
        }
    };

    const lanPortImage: Record<number, string> = {
        1: imageMap.selfcare.internet.reportError.lan1_to_wan,
        2: imageMap.selfcare.internet.reportError.lan2_to_wan,
        3: imageMap.selfcare.internet.reportError.lan3_to_wan,
        4: imageMap.selfcare.internet.reportError.lan4_to_wan,
    };

    const lanPortImageMobile: Record<number, string> = {
        1: imageMap.selfcare.internet.reportError.lan1_to_wan_mobile,
        2: imageMap.selfcare.internet.reportError.lan2_to_wan_mobile,
        3: imageMap.selfcare.internet.reportError.lan3_to_wan_mobile,
        4: imageMap.selfcare.internet.reportError.lan4_to_wan_mobile,
    };

    const hasError = cableError.length > 0;

    return (
        <div className={`input-container${hasError ? ' error' : ''}`} ref={lanPortTopCmponentTop}>
            <ScrollToAnchorPoint id={'cable-section'} className={'id-anchor--selfcare'}/>
            <p>
                Sidder netværkskablet i <strong>fiberboksens LAN{product.lan_port}-port</strong> og i <strong>routerens blå WAN-port</strong>?*
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'cable_connected_yes'}
                    name={'cable_connected'}
                    value={'yes'}
                    onChange={handleRadioChange}
                    checked={flowData.isCableConnected === true}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'cable_connected_yes'}
                >Ja</label>
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'cable_connected_no'}
                    name={'cable_connected'}
                    value={'no'}
                    onChange={handleRadioChange}
                    checked={flowData.isCableConnected === false}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'cable_connected_no'}
                >Nej</label>
            </p>

            {hasError && <p className={'error-message'}>{cableError}</p>}

            {flowData.isCableConnected === false && (
                <div className={'text-box'}>
                    <p>
                        Det er formentlig grunden til, at dit internet ikke virker.
                    </p>
                    <p>
                        Sæt netværkskablet i følgende porte:
                    </p>
                    <ul className={'list list--number'}>
                        <li>
                            <p><strong>LAN{product.lan_port}-port</strong> på fiberboksen.</p>
                        </li>
                        <li>
                            <p>Den <strong>blå WAN-port</strong> på routeren.</p>
                        </li>
                    </ul>
                    {product.lan_port && (
                        <>
                            <img
                                className={'hide-on-small-devices mb-full'}
                                src={lanPortImage[product.lan_port]}
                                alt={'Illustration af hvordan LAN- og WAN-portene skal tilsluttes'}
                            />
                            <img
                                className={'hide-on-large-devices mb-full'}
                                src={lanPortImageMobile[product.lan_port]}
                                alt={'Illustration af hvordan LAN- og WAN-portene skal tilsluttes'}
                            />
                        </>
                    )}
                    <p>
                        Når det er gjort, skal du vente 5 minutter.
                        Hvis dit internet stadig ikke virker efter 5 minutter, skal du vælge &ldquo;Ja&rdquo; ovenfor og fortsætte fejlmeldingen.
                    </p>
                </div>
            )}
        </div>
    );
}

function EnterFiberGponNumber({
                                  flowData,
                                  setFlowData,
                              }: {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
}) {

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let input = event.target.value;

        if (input.length > 64) {
            input = input.substring(0, 64);
        }
        const sanitizedInput = filterGponSerialIllegalCharcters(input);
        setFlowData((prev) => ({ ...prev, gponSerial: sanitizedInput }));
    };

    return (
        <div className={'input-container'}>
            <p>
                Indtast serienummeret på din fiberboks eller vedhæft et billede af det længere nede på siden.
            </p>
            <input
                name={'gponSerial'}
                type={'text'}
                value={flowData.gponSerial}
                onChange={handleInputChange}
                placeholder={'Indtast serienummer'}
            />
            <p className={'small'}>
                Vi bruger det til at dobbelttjekke, at vi har det rigtige nummer registreret.
                Det kan nemlig være årsagen til, at dit internet ikke virker.
            </p>

            <div className={'text-box'}>
                <h4 className={'heading heading--montserrat heading--medium heading--left'}>
                    Hvor kan jeg se serie&shy;nummeret på fiber&shy;boksen?
                </h4>
                <ul className={'list list--number list--image'}>
                    <li>
                        <p>
                            Drej frontpladen af: Hold lås-knappen inde og drej pladen mod uret.
                        </p>
                        <img
                            src={imageMap.selfcare.internet.reportError.fbsa_gpon_1}
                            alt={'Drej frontpladen af ved at holde lås-knappen inde og dreje pladen mod uret.'}
                        />
                    </li>
                    <li>
                        <p>
                            På bagsiden af frontpladen finder du serienummeret ud for <strong>GPON-SN</strong>.
                        </p>
                        <img
                            src={imageMap.selfcare.internet.reportError.fbsa_gpon_2}
                            alt={'Serienummeret står ud for GPON-SN på bagsiden af frontpladen.'}
                        />
                    </li>
                </ul>
            </div>
        </div>
    );
}

function FiberboxLightsTextField({
                                flowData,
                                setFlowData,
                                product,
                                register,
                            }: {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    product: ProductInstance;
    register: RegisterFunction;
}) {
    const fiberboxLightsTextFieldCmponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [fiberBoxError, setFiberBoxError] = useState<string>('');

    useEffect((): void => {
        register("FiberboxLightsTextField",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (flowData.fiberBoxLights === undefined) {
                        setFiberBoxError("Bekræft venligst hvordan din fiberboks lyser.");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    scrollToElement(fiberboxLightsTextFieldCmponentTop.current)
                },
            },
        )
    }, [register, fiberBoxError]);
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'yes';
        setFlowData((prev) => ({ ...prev, fiberBoxLights: value }));

        if (value) {
            setFiberBoxError('');
        }
    };

    const hasError = fiberBoxError.length > 0;

    return (
        <div className={`input-container${hasError ? ' error' : ''}`} ref={fiberboxLightsTextFieldCmponentTop}>
            <ScrollToAnchorPoint id={'fiber-box-section'} className={'id-anchor--selfcare-report-error'}/>
            <p>
                Er der fast grønt lys i{' '}
                <GetFiberboxLampText parent_product_id={product.parent_product_id} />
                ?*
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'fiber_box_green_yes'}
                    name={'fiber_box'}
                    value={'yes'}
                    onChange={handleRadioChange}
                    checked={flowData.fiberBoxLights}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'fiber_box_green_yes'}
                >Ja</label>
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'fiber_box_green_no'}
                    name={'fiber_box'}
                    value={'no'}
                    onChange={handleRadioChange}
                    checked={flowData.fiberBoxLights === false}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'fiber_box_green_no'}
                >Nej</label>
            </p>

            {hasError && <p className={'error-message'}>{fiberBoxError}</p>}
        </div>
    );
}

function GetFiberboxLampText({parent_product_id} : {parent_product_id: number}) {
    switch (parent_product_id) {
        case ProductId.RaaFiberProductId:
            return (
                <>
                    <strong>PON</strong>-lampen på mediekonverteren
                </>
            );

        case ProductId.FbsaProductId:
        case ProductId.GlobalConnectProductId:
            return (
                <>
                    <strong>&lt;&gt;</strong>-lampen på din fiberboks
                </>
            );

        case ProductId.EnergiFynProductId:
        case ProductId.FibiaProductId:
            return (
                <>
                    <strong>globus</strong>-lampen på din fiberboks
                </>
            );

        case ProductId.SydEnergiProductId:
            return (
                <>
                    <strong>GPON</strong>-lampen på din fiberboks
                </>
            );

        case ProductId.EwiiProductId:
            return (
                <>
                    <strong>WAN</strong>-/<strong>globus</strong>-lampen på din fiberboks
                </>
            );

        case ProductId.EniigProductId:
        case ProductId.NordEnergiProductId:
        case ProductId.ThyMorsProductId:
        case ProductId.SefProductId:
        case ProductId.RahProductId:
            return (
                <>
                    <strong>GPON</strong>-, <strong>globus</strong>- eller <strong>fiber</strong>-lampen på din fiberboks
                </>
            );

        default:
            return (
                <>
                    <strong>internet</strong>-lampen på din fiberboks
                </>
            );
    }
}
